"use client";

import React from 'react';
import { usePathname } from 'next/navigation';

import { useAppContext } from '@components/Provider';

import Sites from 'src/constants/Sites';
import NextImage from '@components/Image/NextImage';
import Meta from '@components/Meta';
import Entity from '@components/Entity';
import RenderSearchPost from '@components/ContentPost/ContentPost';
import Pagination from '@components/Pagination/Pagination';
import { _isFirstPage, _isLastPage, appendHttpsToUrl, getBasePageLink, getLastPageNumber, getPageNumberAsInt, getPaginationText, nextLink, previousLink } from 'helpers/Helper';

import styles from "./Author.module.css";
import "./Author.css";

const populateSEO = (author, SiteSettings) => {
  const SEO_Data = {
    Title: `${author ? `${author.display_name} - Author Profile and Articles | ${SiteSettings.site_name}` : ' '}`,
    Description: author?.short_bio || ' ',
    SEOTitle: author?.seotitle?.[0]?.seotitle || '',
    SocialTitle: author?.socialtitle?.[0]?.socialtitle || '',
  };
  return SEO_Data;
};

const AuthorPage = (props) => {
  const pathname = usePathname();
  const {data, host, siteId, isMobile, hideEBBNl} = useAppContext();

  const {SiteSettings} = data || {};
  const author = props?.data[0]?.AuthorPage?.contributor[0] || {};
  const [authorDetails] = props?.data || [];

  const renderAuthorDetails = () => {
    return (
      author && (
        <div className={styles["authorDetails"]}>
          {author.image &&
            <div className={styles["bio-image"]}>
              <NextImage
                ImageUrl={author.image}
                Alt_Text="author-image"
                width={100}
                height={100}
              />
            </div>
          }
          <div className={[styles["section"], !author.image && styles["sectionNoMargin"]].join(" ")}>
            <h1>{author.display_name}</h1>
            <div dangerouslySetInnerHTML={{ __html: author.short_bio }}></div>
            <p>
              {author.website &&
                <a rel="noopener noreferrer" target="_blank" href={appendHttpsToUrl(author.website)}>
                  {author.website.replace(/(^\w+:|^)\/\//, '').split('/')[0]}
                </a>
              }
              {author.website && author.social_media_name && <span>|</span>}
              {author.social_media_name && 
                <a rel="noopener noreferrer" target="_blank" href={author.social_media_link ? appendHttpsToUrl(author.social_media_link) : ''}>
                  @{author.social_media_name}
                </a>
              }
            </p>
          </div>
        </div>
      )
    )
  }

  const renderResults = () => {
    const resultData = authorDetails?.AuthorPage;
    const categoryLayout = SiteSettings?.Category_Layout;
    const { total } = authorDetails?.AuthorPage || {total: 0};
    const route = pathname;
    const slugName = props.slug ? decodeURIComponent(props.slug) : '';
    const pageSize = props.pageSize;
    const currentPageNumber = getPageNumberAsInt(props.pageNumber);
    const isFirstPage = _isFirstPage(currentPageNumber);
    const isLastPage = _isLastPage(currentPageNumber, total, pageSize);

    if(!resultData.posts || isNaN(currentPageNumber) || currentPageNumber > getLastPageNumber(total)){
      return <div></div>;
    }
    
    return (
      <div className={[resultData.displayName, resultData.category && "CategoryPage", categoryLayout && `alt-${categoryLayout}`].join(" ")}>
        <Meta
          url={props.pageUrl}
          static={populateSEO(author, SiteSettings)}
          pageType={Entity.STATIC}
          siteName={SiteSettings.site_name}
          domain={host}
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
          previousLink={previousLink(props.pageName, slugName, currentPageNumber)}
          nextLink={nextLink(props.pageName, slugName, currentPageNumber)}
          noIndex={isFirstPage ? false : true}
          isNewAppRouter={true}
        />
        <div className="postCardContainer">
          <RenderSearchPost props={{ ...resultData, isCategoryPage: false }} />
        </div>
        <Pagination
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
          basePageLink={getBasePageLink(props.pageName, slugName)}
          currentPageNumber={currentPageNumber}
          lastPageNumber={getLastPageNumber(total, pageSize)}
          getLinkHref={route}
          paginationText={getPaginationText(total, currentPageNumber, pageSize, 'results')}
          pageSize={pageSize}
        />
      </div>
    )
  }

  return (
    <div className={[styles["authorSection"], 
                      isMobile && !hideEBBNl && styles["authorSectionMobile"], 
                      siteId !== Sites.EARLYBIRDBOOKS && siteId !== Sites.OPENROADMEDIA && styles["authorSectionContent"], 
                      "topPadding"].join(" ")}>
      {renderAuthorDetails()}
      <div className="row">
        <div className="cardSection">
          {renderResults()}
        </div>
      </div>
    </div>
  )
}

export default AuthorPage